import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { SettingsService } from '@api/services/settings.service';
import { Settings } from '@api/models/SettingsApi/Contract/settings';
import { Observable } from 'rxjs';
import { SlistData } from '@api/models/SettingsApi/Contract/slist-data';
import { shareReplay, tap } from 'rxjs/operators';
import { SetSettingRequest } from '@api/models/SettingsApi/Contract/set-setting-request';
import { CustomRxState } from '@core/state/rx.state';


interface IParametersState {
    loading: Record<StartWithUnit, boolean>;
    settings: Settings[];
    settingsParamList: Map<number, Observable<SlistData[]>>;
}

interface ParametersActions {
    requestSettings: Settings[];
}

@Injectable()
export class ParametersState extends CustomRxState<IParametersState> {
    readonly #actions: RxActions<ParametersActions> = new RxActionFactory<ParametersActions>().create();
    readonly #settingsService: SettingsService = inject(SettingsService);

    public readonly settings$: Observable<Settings[]> = this.select('settings');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestSettings();
    }

    public requestSettings(): void {
        this.hold(
            this.#settingsService.apiSettingsListSettingsGet(),
            this.#actions.requestSettings,
        );
    }

    public requestParamList(slistId: number): Observable<SlistData[]> {
        const settingsParamList: Map<number, Observable<SlistData[]>> = this.get('settingsParamList');

        if (settingsParamList && settingsParamList.has(slistId)) {
            return settingsParamList.get(slistId);
        } else {
            const requestSettingsList: Observable<SlistData[]> =
                this.#settingsService.apiSettingsListSlistDataGet({ slistId }).pipe(
                    shareReplay(
                        {
                            bufferSize: 1,
                            refCount: true,
                        },
                    ),
                );

            settingsParamList.set(slistId, requestSettingsList);

            this.set({ settingsParamList });

            return requestSettingsList;
        }
    }

    public updateSettings(body: SetSettingRequest[]): Observable<void> {
        return this.#settingsService.apiSettingsSetSettingPost({ body }).pipe(
            tap(() => this.requestSettings()),
        );
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            settings: null,
            settingsParamList: new Map<number, Observable<SlistData[]>>(),
        });
    }

    private connectSelectors(): void {
        this.connect('settings', this.#actions.requestSettings$);
    }
}
